import { alertService } from '@services/alert/alert.service';
import { UiService } from './../../../shared/services/ui/ui.service';
import { LogsService } from '@services/logs/logs.service';
import { Component, OnInit } from '@angular/core';
import { PaginationRequest } from '@classes/pagination/pagination-request';
import { Pagination } from '@classes/pagination/pagination';
import { DatatableBaseComponent } from '@components/datatable-base.component';
import { MatTableDataSource } from '@angular/material/table';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { LicenseService } from '@services/anpr/license.service';

interface Etat {
  value: string;
  viewValue: string;
}

@Component({
  selector: 'app-monitoring-logs',
  templateUrl: './monitoring-logs.component.html',
  styleUrls: ['./monitoring-logs.component.scss'],
})
export class MonitoringLogsComponent
  extends DatatableBaseComponent
  implements OnInit
{
  filterUserLoginsValue = null;
  filterAlertValue = null;
  filterAnprCallValue = null;
  paginationUserLogins: Pagination = {
    pageNumber: 1,
    pageSize: 10,
    totalRecords: null,
    totalPages: null,
  };
  paginationAlerts: Pagination = {
    pageNumber: 1,
    pageSize: 10,
    totalRecords: null,
    totalPages: null,
  };
  paginationAnprCalls: Pagination = {
    pageNumber: 1,
    pageSize: 10,
    totalRecords: null,
    totalPages: null,
  };
  sortValue: string;
  sortValueAlerts: string;
  sortValueAnprCalls: string;

  userLogins = null;
  alerts = null;
  anprCalls = null;
  dataSourceUserLogins: MatTableDataSource<any>;
  dataSourceAlerts: MatTableDataSource<any>;
  dataSourceAnprCalls: MatTableDataSource<any>;
  displayedColumnsUserLogins = ['username', 'date'];
  displayedColumnsAlerts = [
    'date',
    'camera',
    'status',
    'etat',
    'location',
    'modifiedOn',
    'modifiedBy',
  ];
  displayedColumnsAnprCalls = [
    'client',
    'date',
    'user',
    'imageName',
    'numberPlate',
    'textDetected',
    'resultName',
    'userAnswer',
  ];

  filterAlertForm = new UntypedFormGroup({
    from: new UntypedFormControl(),
    to: new UntypedFormControl(),
    state: new UntypedFormControl(),
  });

  filterLoginsForm = new UntypedFormGroup({
    from: new UntypedFormControl(),
    to: new UntypedFormControl(),
  });

  filterAnprCallsForm = new UntypedFormGroup({
    from: new UntypedFormControl(),
    to: new UntypedFormControl(),
  });

  isExportingAlerts = false;
  isExportingUserLogins = false;
  isExportingAnprCalls = false;
  isLoadingAlerts = false;
  isLoadingUserLogins = false;
  isLoadingAnprCalls = false;

  etats: Etat[] = [
    { value: '', viewValue: 'None' },
    { value: 'UnTreated', viewValue: 'UnTreated' },
    { value: 'InProgress', viewValue: 'InProgress' },
    { value: 'Treated', viewValue: 'Treated' },
  ];

  constructor(
    private logsSrv: LogsService,
    private alertsSrv: alertService,
    private anprSrv: LicenseService,
    protected uiSrv: UiService
  ) {
    super(uiSrv);
  }

  ngOnInit(): void {
    this.initSub();
  }

  initSub(): void {
    this.addSubscription(
      this.logsSrv.userLoginsListChanged.subscribe((userLogins) => {
        this.userLogins = userLogins;
        this.initTableUserLogins();
      })
    );

    this.addSubscription(
      this.logsSrv.paginationSubject.subscribe((pagination) => {
        if (pagination) {
          this.paginationUserLogins = pagination;
        }
      })
    );

    this.addSubscription(
      this.alertsSrv.alertListChanged.subscribe((alerts) => {
        this.alerts = alerts;
        this.initTableAlerts();
      })
    );

    this.addSubscription(
      this.alertsSrv.paginationSubject.subscribe((pagination) => {
        if (pagination) {
          this.paginationAlerts = pagination;
        }
      })
    );

    this.addSubscription(
      this.anprSrv.callListChanged.subscribe((calls) => {
        this.anprCalls = calls;
        this.initTableAnprCalls();
      })
    );

    this.addSubscription(
      this.anprSrv.paginationSubject.subscribe((pagination) => {
        if (pagination) {
          this.paginationAnprCalls = pagination;
        }
      })
    );
  }

  // USER LOGINS

  initTableUserLogins(): void {
    this.dataSourceUserLogins = new MatTableDataSource(this.userLogins);
    this.dataSourceUserLogins.sort = this.sort;

    this.isLoadingUserLogins = false;
  }

  onFilter(): void {
    const formValue = this.filterLoginsForm.value;

    this.filterUserLoginsValue = formValue;
    this.getListUserLogins();
  }

  getListUserLogins(): void {
    this.isLoadingUserLogins = true;

    const newRequest = new PaginationRequest(
      this.filterUserLoginsValue,
      this.sortValue,
      this.paginationUserLogins.pageNumber,
      this.paginationUserLogins.pageSize
    );

    this.logsSrv.getUserLogins(newRequest);
  }

  clearLoginsFilters(): void {
    this.filterLoginsForm.reset();
    this.userLogins = null;
  }

  onPageChanged(pageEvent: PageEvent): void {
    this.paginationUserLogins.pageNumber = pageEvent.pageIndex + 1;
    this.getListUserLogins();
  }

  onSortChanged(sortEvent: Sort): void {
    let direction = '';

    if (sortEvent.direction === 'asc') {
      direction = '';
    } else if (sortEvent.direction === 'desc') {
      direction = '-';
    }
    if (sortEvent.direction === '') {
      this.sortValue = null;
    } else {
      this.sortValue = ` ${direction}${sortEvent.active}`;
    }

    this.getListUserLogins();
  }

  exportUserLogins(): void {
    this.isExportingUserLogins = true;
    const formValue = this.filterLoginsForm.value;

    this.filterUserLoginsValue = formValue;

    const newRequest = new PaginationRequest(
      this.filterUserLoginsValue,
      null,
      null,
      null
    );
    this.logsSrv.exportExcelUserLogins(newRequest).subscribe(() => {
      this.isExportingUserLogins = false;
    });
  }

  // ALERT LOGS

  initTableAlerts(): void {
    this.dataSourceAlerts = new MatTableDataSource(this.alerts);
    this.dataSourceAlerts.sort = this.sort;

    this.isLoadingAlerts = false;
  }

  onFilterAlerts(): void {
    const formValue = this.filterAlertForm.value;

    this.filterAlertValue = formValue;
    this.getListAlerts();
  }

  getListAlerts(): void {
    this.isLoadingAlerts = true;

    const newRequest = new PaginationRequest(
      this.filterAlertValue,
      this.sortValueAlerts,
      this.paginationAlerts.pageNumber,
      this.paginationAlerts.pageSize
    );

    this.alertsSrv.getAlertsWithPagination(newRequest);
  }

  clearFiltersAlerts(): void {
    this.filterAlertForm.reset();
    this.alerts = null;
  }

  onPageChangedAlerts(pageEvent: PageEvent): void {
    this.paginationAlerts.pageNumber = pageEvent.pageIndex + 1;
    this.getListAlerts();
  }

  onSortChangedAlerts(sortEvent: Sort): void {
    let direction = '';

    if (sortEvent.direction === 'asc') {
      direction = '';
    } else if (sortEvent.direction === 'desc') {
      direction = '-';
    }
    if (sortEvent.direction === '') {
      this.sortValueAlerts = null;
    } else {
      this.sortValueAlerts = ` ${direction}${sortEvent.active}`;
    }

    this.getListAlerts();
  }

  exportAlertLogs(): void {
    this.isExportingAlerts = true;
    const formValue = this.filterAlertForm.value;

    this.filterAlertValue = formValue;

    const newRequest = new PaginationRequest(
      this.filterAlertValue,
      null,
      null,
      null
    );
    this.alertsSrv.exportAlert(newRequest).subscribe(() => {
      this.isExportingAlerts = false;
    });
  }

  // ANPR CALLS

  initTableAnprCalls(): void {
    this.dataSourceAnprCalls = new MatTableDataSource(this.anprCalls);
    this.dataSourceAnprCalls.sort = this.sort;

    this.isLoadingAnprCalls = false;
  }

  onFilterAnprCalls(): void {
    const formValue = this.filterAnprCallsForm.value;

    this.filterAnprCallValue = formValue;
    this.getListAnprCalls();
  }

  getListAnprCalls(): void {
    this.isLoadingAnprCalls = true;

    const newRequest = new PaginationRequest(
      this.filterAnprCallValue,
      this.sortValueAnprCalls,
      this.paginationAnprCalls.pageNumber,
      this.paginationAnprCalls.pageSize
    );

    this.anprSrv.getAnprCallsWithPagination(newRequest);
  }

  clearFiltersAnprCalls(): void {
    this.filterAnprCallsForm.reset();
    this.anprCalls = null;
  }

  onPageChangedAnprCalls(pageEvent: PageEvent): void {
    this.paginationAnprCalls.pageNumber = pageEvent.pageIndex + 1;
    this.getListAnprCalls();
  }

  onSortChangedAnprCalls(sortEvent: Sort): void {
    let direction = '';

    if (sortEvent.direction === 'asc') {
      direction = '';
    } else if (sortEvent.direction === 'desc') {
      direction = '-';
    }
    if (sortEvent.direction === '') {
      this.sortValueAnprCalls = null;
    } else {
      this.sortValueAnprCalls = ` ${direction}${sortEvent.active}`;
    }

    this.getListAnprCalls();
  }

  exportAnprCallsLogs(): void {
    this.isExportingAnprCalls = true;
    const formValue = this.filterAnprCallsForm.value;

    this.filterAnprCallValue = formValue;

    const newRequest = new PaginationRequest(
      this.filterAnprCallValue,
      null,
      null,
      null
    );
    this.anprSrv.exportAnprCall(newRequest).subscribe(() => {
      this.isExportingAnprCalls = false;
    });
  }
}
